import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ChevronDown, Download, TrendingUp, TrendingDown } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip, Cell, LineChart, Line, Legend } from "recharts";
import { toast } from "sonner";

const PERIODS = ["daily", "weekly", "monthly", "quarterly", "yearly"];
const prettyMat = (m) => (m || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function Reports() {
  const [period, setPeriod] = useState("monthly");
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = (p) => {
    setBusy(true);
    api.get(`/reports/feasibility?period=${p}`).then(r => setData(r.data)).catch(() => {}).finally(() => setBusy(false));
  };
  useEffect(() => { load(period); }, [period]);
  useEffect(() => { api.get("/reports/profit-trend?months=12").then(r => setTrend(r.data.series)).catch(() => {}); }, []);

  const download = async () => {
    try {
      const res = await api.get(`/export/reports/feasibility?period=${period}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `sango_feasibility_${period}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Report downloaded");
    } catch { toast.error("Export failed"); }
  };

  if (!data) return <div className="p-8 text-[#6E6E6A]">Loading…</div>;

  const profitPositive = data.profit >= 0;
  const costData = [
    { name: "Material", value: data.costs.material },
    { name: "Fuel", value: data.costs.fuel },
    { name: "Labor", value: data.costs.labor },
  ];
  const matBreakdown = Object.entries(data.costs.material_breakdown || {}).map(([k, v]) => ({ name: prettyMat(k), value: v }));

  return (
    <div className="p-4 sm:p-8 grain-bg">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E6E6A]">Admin only · Feasibility & Cost</div>
            <h1 className="font-display text-4xl tracking-tighter font-black">Reports</h1>
            <p className="text-[#6E6E6A] text-sm mt-2">Range: <span className="text-[#1C1C1A]">{data.range.start} → {data.range.end}</span></p>
          </div>
          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2 capitalize" data-testid="reports-period">{period}<ChevronDown className="w-4 h-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {PERIODS.map(p => <DropdownMenuItem key={p} onClick={() => setPeriod(p)} className="capitalize">{p}</DropdownMenuItem>)}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button onClick={download} data-testid="reports-export" className="gap-2 bg-[#1C1C1A] hover:bg-black text-white">
              <Download className="w-4 h-4" /> Excel
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          <Card className="p-5 border border-[#D8D8D5] shadow-none rounded-md">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E6E6A]">Revenue</div>
            <div className="font-display text-3xl font-black tracking-tighter mt-2 text-[#5B7B61]">₹{data.revenue.toLocaleString('en-IN')}</div>
            <div className="text-xs text-[#6E6E6A] mt-1">{data.dispatch_count} dispatches</div>
          </Card>
          <Card className="p-5 border border-[#D8D8D5] shadow-none rounded-md">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E6E6A]">Total Cost</div>
            <div className="font-display text-3xl font-black tracking-tighter mt-2 text-[#C85A42]">₹{data.costs.total.toLocaleString('en-IN')}</div>
            <div className="text-xs text-[#6E6E6A] mt-1">Mat + Fuel + Labor</div>
          </Card>
          <Card className="p-5 border border-[#D8D8D5] shadow-none rounded-md">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E6E6A]">Profit</div>
            <div className={`font-display text-3xl font-black tracking-tighter mt-2 ${profitPositive ? "text-[#1C1C1A]" : "text-[#C85A42]"}`}>
              {profitPositive ? "" : "-"}₹{Math.abs(data.profit).toLocaleString('en-IN')}
            </div>
            <div className="text-xs text-[#6E6E6A] mt-1 inline-flex items-center gap-1">
              {profitPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {data.margin_pct}% margin
            </div>
          </Card>
          <Card className="p-5 border border-[#D8D8D5] shadow-none rounded-md">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E6E6A]">Cost / unit</div>
            <div className="font-display text-3xl font-black tracking-tighter mt-2 text-[#1C1C1A]">₹{data.cost_per_unit}</div>
            <div className="text-xs text-[#6E6E6A] mt-1">{data.production_units} units produced</div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <Card className="p-6 border border-[#D8D8D5] shadow-none rounded-md">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E6E6A]">Cost composition</div>
            <div className="h-64 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={costData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E6" />
                  <XAxis dataKey="name" stroke="#6E6E6A" fontSize={11} />
                  <YAxis stroke="#6E6E6A" fontSize={11} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #D8D8D5" }} />
                  <Bar dataKey="value">
                    {costData.map((_, i) => <Cell key={i} fill={["#2A2A28", "#C85A42", "#5B7B61"][i]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 text-xs text-[#6E6E6A] space-y-1">
              <div>Fuel: {data.costs.fuel_liters} L</div>
              <div>Labor: {data.costs.labor_hours} hours</div>
            </div>
          </Card>

          <Card className="p-6 border border-[#D8D8D5] shadow-none rounded-md">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E6E6A]">Material cost breakdown</div>
            <div className="h-64 mt-4">
              {matBreakdown.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-[#6E6E6A]">Set material rates in Costs to see breakdown</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={matBreakdown} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E6" />
                    <XAxis type="number" stroke="#6E6E6A" fontSize={10} />
                    <YAxis type="category" dataKey="name" stroke="#6E6E6A" fontSize={10} width={110} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #D8D8D5" }} />
                    <Bar dataKey="value" fill="#C85A42" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>
        </div>

        {trend && trend.length > 0 && (
          <Card className="p-6 border border-[#D8D8D5] shadow-none rounded-md mt-4">
            <div className="flex items-center justify-between">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E6E6A]">Profit trend · last 12 months</div>
              <div className="text-xs text-[#6E6E6A]">Revenue vs Cost vs Profit</div>
            </div>
            <div className="h-80 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E6" />
                  <XAxis dataKey="month" stroke="#6E6E6A" fontSize={10} />
                  <YAxis stroke="#6E6E6A" fontSize={10} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #D8D8D5" }} formatter={(v) => `₹${Number(v).toLocaleString('en-IN')}`} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="revenue" stroke="#5B7B61" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="cost" stroke="#C85A42" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="profit" stroke="#1C1C1A" strokeWidth={2.5} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
